import React from 'react';
import { Database, Wifi, Clock, AlertTriangle, Info, XCircle, AlertCircle, Search, RefreshCw, Lightbulb } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { LEAD_TABLE_STATUS, ErrorDisplayInfo } from '../../types';

interface EmptyLeadStateProps {
  tableStatus: string;
  errorMessage?: string;
  prospectsAnalyzed?: number | null;
}

// Helper function to map backend error status to frontend display information
const getErrorDisplayInfo = (status: string, errorMessage?: string): ErrorDisplayInfo => {
  const fallbackMessages: Record<string, ErrorDisplayInfo> = {
    [LEAD_TABLE_STATUS.ERROR_PROVIDER_API]: {
      title: 'Connection Issue',
      message: errorMessage || 'Unable to connect to our lead generation service. This is usually temporary.',
      icon: 'Wifi',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      suggestedAction: 'Please try again'
    },
    [LEAD_TABLE_STATUS.ERROR_TIMEOUT]: {
      title: 'Search Timed Out',
      message: errorMessage || 'Lead generation took longer than expected and timed out.',
      icon: 'Clock',
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      suggestedAction: 'Please try again'
    },
    [LEAD_TABLE_STATUS.COMPLETED_NO_RESULTS]: {
      title: 'No Matches Found',
      message: errorMessage || 'No matching results found for your search query.',
      icon: 'Database',
      color: 'text-gray-500',
      bgColor: 'bg-gray-50',
      suggestedAction: 'Try a new search'
    },
    [LEAD_TABLE_STATUS.ERROR_RATE_LIMIT]: {
      title: 'Too Many Requests',
      message: errorMessage || 'Too many requests in a short period.',
      icon: 'AlertCircle',
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      suggestedAction: 'Please try again'
    },
    [LEAD_TABLE_STATUS.ERROR_INVALID_QUERY]: {
      title: 'Invalid Query',
      message: errorMessage || 'Your search query could not be processed.',
      icon: 'Info',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      suggestedAction: 'Please try again'
    },
    [LEAD_TABLE_STATUS.ERROR_SERVER]: {
      title: 'Server Error',
      message: errorMessage || 'An unexpected error occurred on our servers.',
      icon: 'XCircle',
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      suggestedAction: 'Please try again'
    },
    [LEAD_TABLE_STATUS.ERROR]: {
      title: 'Error',
      message: errorMessage || 'An error occurred during lead generation.',
      icon: 'AlertTriangle',
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      suggestedAction: 'Please try again'
    },
    [LEAD_TABLE_STATUS.ERROR_CONFIG]: {
      title: 'Configuration Error',
      message: errorMessage || 'There was a configuration error.',
      icon: 'AlertTriangle',
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      suggestedAction: 'Contact support'
    }
  };

  return fallbackMessages[status] || fallbackMessages[LEAD_TABLE_STATUS.ERROR];
};

// Helper function to get the icon component from icon name string
const getIconComponent = (iconName: string) => {
  const iconMap: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
    'Wifi': Wifi,
    'Clock': Clock,
    'Database': Database,
    'AlertCircle': AlertCircle,
    'Info': Info,
    'XCircle': XCircle,
    'AlertTriangle': AlertTriangle
  };
  return iconMap[iconName] || AlertTriangle;
};

export const EmptyLeadState: React.FC<EmptyLeadStateProps> = ({
  tableStatus,
  errorMessage,
  prospectsAnalyzed
}) => {
  const navigate = useNavigate();

  // Handle "no results" scenario - Clean, minimal design
  if (tableStatus === LEAD_TABLE_STATUS.COMPLETED_NO_RESULTS) {
    return (
      <div className="flex items-center justify-center bg-white min-h-[400px]">
        <div className="max-w-sm mx-auto px-6 py-10">
          {/* Minimal icon */}
          <div className="flex justify-center mb-5">
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
              <Search className="w-5 h-5 text-gray-400" strokeWidth={1.5} />
            </div>
          </div>

          {/* Title */}
          <h3 className="text-[15px] font-semibold text-gray-900 text-center mb-2">
            No matches found
          </h3>

          {/* Description */}
          <p className="text-[13px] text-gray-500 text-center leading-relaxed mb-6">
            {prospectsAnalyzed
              ? `Searched ${prospectsAnalyzed.toLocaleString()} prospects with no matches.`
              : 'No results match your current criteria.'
            }
          </p>

          {/* Suggestions card */}
          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Lightbulb className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">Suggestions</span>
            </div>
            <ul className="space-y-2">
              <li className="flex items-start gap-2 text-[12px] text-gray-600">
                <span className="text-gray-300 mt-0.5">•</span>
                <span>Use broader keywords or fewer criteria</span>
              </li>
              <li className="flex items-start gap-2 text-[12px] text-gray-600">
                <span className="text-gray-300 mt-0.5">•</span>
                <span>Try alternative industry terms</span>
              </li>
              <li className="flex items-start gap-2 text-[12px] text-gray-600">
                <span className="text-gray-300 mt-0.5">•</span>
                <span>Expand your geographic scope</span>
              </li>
            </ul>
          </div>

          {/* Action button */}
          <button
            onClick={() => navigate('/datasets')}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-900 text-white text-[13px] font-medium rounded-lg hover:bg-gray-800 transition-colors"
          >
            <Search className="w-4 h-4" />
            Start new search
          </button>
        </div>
      </div>
    );
  }

  // Handle error scenarios - Clean error state
  if (tableStatus && tableStatus.startsWith('error')) {
    const errorInfo = getErrorDisplayInfo(tableStatus, errorMessage);
    const IconComponent = getIconComponent(errorInfo.icon);

    return (
      <div className="flex items-center justify-center bg-white min-h-[400px]">
        <div className="max-w-sm mx-auto px-6 py-10">
          {/* Error icon */}
          <div className="flex justify-center mb-5">
            <div className={`w-12 h-12 rounded-full ${errorInfo.bgColor} flex items-center justify-center`}>
              <IconComponent className={`w-5 h-5 ${errorInfo.color}`} strokeWidth={1.5} />
            </div>
          </div>

          {/* Title */}
          <h3 className="text-[15px] font-semibold text-gray-900 text-center mb-2">
            {errorInfo.title}
          </h3>

          {/* Error message */}
          <p className="text-[13px] text-gray-500 text-center leading-relaxed mb-6">
            {errorInfo.message}
          </p>

          {/* Action buttons */}
          <div className="flex gap-3">
            <button
              onClick={() => window.location.reload()}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 text-[13px] font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
            <button
              onClick={() => navigate('/datasets')}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-900 text-white text-[13px] font-medium rounded-lg hover:bg-gray-800 transition-colors"
            >
              <Search className="w-4 h-4" />
              New search
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Default empty state - Minimal and clean
  return (
    <div className="flex items-center justify-center bg-white min-h-[400px]">
      <div className="max-w-sm mx-auto px-6 py-10">
        {/* Icon */}
        <div className="flex justify-center mb-5">
          <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
            <Database className="w-5 h-5 text-gray-400" strokeWidth={1.5} />
          </div>
        </div>

        {/* Title */}
        <h3 className="text-[15px] font-semibold text-gray-900 text-center mb-2">
          No data yet
        </h3>

        {/* Description */}
        <p className="text-[13px] text-gray-500 text-center leading-relaxed mb-6">
          This dataset is empty. Start by searching for leads or uploading a CSV file.
        </p>

        {/* Action button */}
        <button
          onClick={() => navigate('/datasets')}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-900 text-white text-[13px] font-medium rounded-lg hover:bg-gray-800 transition-colors"
        >
          <Search className="w-4 h-4" />
          Start new search
        </button>
      </div>
    </div>
  );
};
